'use client';

import type { CSSProperties, ReactNode } from 'react';

import { Drawer } from 'vaul';

import { cn } from '../utils/cn';

import { useBlockUnderlyingPointerEvents } from './use-block-pointer-events';

const PADDING_MAP = {
	comfortable: '64px',
	compact: '40px',
} as const;

interface BaseDrawerProps {
	open: boolean;
	onClose: () => void;
	/** Accessibility title (rendered sr-only) */
	title: string;
	children: ReactNode;
	/** Safe-area bottom padding preset (default: comfortable = 64px) */
	padding?: 'comfortable' | 'compact';
	/** Override max height (e.g. '85dvh') */
	maxHeight?: string;
	/** Vaul dismissible prop */
	dismissible?: boolean;
	/** Render as a nested drawer (stacks over a parent drawer). Requires an ancestor Drawer.Root. */
	nested?: boolean;
	/** Stop pointer events from propagating through the portal */
	stopPointerPropagation?: boolean;
	/** Extra className on Drawer.Content */
	className?: string;
	/** Extra inline styles on Drawer.Content */
	contentStyle?: CSSProperties;
}

export const BaseDrawer = ({
	open,
	onClose,
	title,
	children,
	padding = 'comfortable',
	maxHeight,
	dismissible,
	nested = false,
	stopPointerPropagation = false,
	className,
	contentStyle,
}: BaseDrawerProps) => {
	useBlockUnderlyingPointerEvents(open);

	// NestedRoot reads the parent drawer's context to scale it back as this one
	// opens; it throws if there is no ancestor Drawer.Root, hence the flag.
	const Root = nested ? Drawer.NestedRoot : Drawer.Root;

	const pointerHandlers = stopPointerPropagation
		? {
				onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
				onPointerMove: (e: React.PointerEvent) => e.stopPropagation(),
				onPointerUp: (e: React.PointerEvent) => e.stopPropagation(),
			}
		: {};

	return (
		<Root
			open={open}
			onOpenChange={(o) => !o && onClose()}
			dismissible={dismissible}
			modal
		>
			<Drawer.Portal>
				<Drawer.Overlay className="fixed inset-0 z-[9999] bg-backdrop" />
				<Drawer.Content
					className={cn(
						'fixed inset-x-0 bottom-0 z-[9999]',
						'flex flex-col',
						'rounded-t-2xl',
						'border-t border-x border-border-weak bg-overlay backdrop-blur-[20px] shadow-floating',
						'focus:outline-none',
						className,
					)}
					style={{
						paddingBottom: `max(env(safe-area-inset-bottom), ${PADDING_MAP[padding]})`,
						maxHeight,
						...contentStyle,
					}}
					{...pointerHandlers}
				>
					<Drawer.Title className="sr-only">{title}</Drawer.Title>
					<Drawer.Handle className="mt-2 mb-2 shrink-0 bg-neutral/25" />
					{children}
				</Drawer.Content>
			</Drawer.Portal>
		</Root>
	);
};
